/* global google */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from '@vis.gl/react-google-maps/dist/index.umd';

const eventMap = {
  onClick: 'click',
  onDblClick: 'dblclick',
  onDrag: 'drag',
  onDragEnd: 'dragend',
  onDragStart: 'dragstart',
  onMouseDown: 'mousedown',
  onMouseMove: 'mousemove',
  onMouseOut: 'mouseout',
  onMouseOver: 'mouseover',
  onMouseUp: 'mouseup',
  onRightClick: 'rightclick',
};

const Polygon = ({
  options,
  draggable,
  editable,
  visible,
  path,
  paths,
  onDblClick,
  onDragEnd,
  onDragStart,
  onMouseDown,
  onMouseMove,
  onMouseOut,
  onMouseOver,
  onMouseUp,
  onRightClick,
  onClick,
  onDrag,
  onLoad,
  onUnmount,
}) => {
  const map = useMap();
  const [polygon, setPolygon] = useState(null);
  const listenersRef = useRef([]);

  const eventHandlers = useMemo(
    () => ({
      onClick,
      onDblClick,
      onDrag,
      onDragEnd,
      onDragStart,
      onMouseDown,
      onMouseMove,
      onMouseOut,
      onMouseOver,
      onMouseUp,
      onRightClick,
    }),
    [
      onClick,
      onDblClick,
      onDrag,
      onDragEnd,
      onDragStart,
      onMouseDown,
      onMouseMove,
      onMouseOut,
      onMouseOver,
      onMouseUp,
      onRightClick,
    ]
  );

  useEffect(() => {
    if (!map) return undefined;

    const polygonInstance = new google.maps.Polygon({
      ...(options || {}),
      map,
    });

    if (path) {
      polygonInstance.setPath(path);
    }

    if (paths) {
      polygonInstance.setPaths(paths);
    }

    if (typeof visible !== 'undefined') {
      polygonInstance.setVisible(visible);
    }

    if (typeof editable !== 'undefined') {
      polygonInstance.setEditable(editable);
    }

    if (typeof draggable !== 'undefined') {
      polygonInstance.setDraggable(draggable);
    }

    setPolygon(polygonInstance);
    if (onLoad) {
      onLoad(polygonInstance);
    }

    return () => {
      listenersRef.current.forEach((listener) =>
        google.maps.event.removeListener(listener)
      );
      listenersRef.current = [];

      if (onUnmount) {
        onUnmount(polygonInstance);
      }
      polygonInstance.setMap(null);
      setPolygon(null);
    };
  }, [map, draggable, editable, onLoad, onUnmount, options, path, paths, visible]);

  useEffect(() => {
    if (!polygon || !options) return;
    polygon.setOptions(options);
  }, [polygon, options]);

  useEffect(() => {
    if (!polygon) return;
    if (typeof draggable !== 'undefined') {
      polygon.setDraggable(draggable);
    }
  }, [polygon, draggable]);

  useEffect(() => {
    if (!polygon) return;
    if (typeof editable !== 'undefined') {
      polygon.setEditable(editable);
    }
  }, [polygon, editable]);

  useEffect(() => {
    if (!polygon) return;
    if (typeof visible !== 'undefined') {
      polygon.setVisible(visible);
    }
  }, [polygon, visible]);

  useEffect(() => {
    if (!polygon || typeof path === 'undefined') return;
    polygon.setPath(path);
  }, [polygon, path]);

  useEffect(() => {
    if (!polygon || typeof paths === 'undefined') return;
    polygon.setPaths(paths);
  }, [polygon, paths]);

  useEffect(() => {
    if (!polygon) return;

    listenersRef.current.forEach((listener) =>
      google.maps.event.removeListener(listener)
    );
    listenersRef.current = [];

    Object.entries(eventMap).forEach(([propName, eventName]) => {
      const handler = eventHandlers[propName];
      if (handler) {
        listenersRef.current.push(
          google.maps.event.addListener(polygon, eventName, handler)
        );
      }
    });
  }, [polygon, eventHandlers]);

  return null;
};

export default Polygon;

